import React, { useState } from 'react';
import { ETFOverlapData } from '../../services/portfolio';
import LoadingSpinner from '../ui/LoadingSpinner';
import ETFSimilarityHeatmap from './ETFSimilarityHeatmap';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { useCurrency } from '../../contexts/CurrencyContext';

interface ETFOverlapSectionProps {
  overlapData: ETFOverlapData | undefined;
  isLoading: boolean;
}

const ETFOverlapSection: React.FC<ETFOverlapSectionProps> = ({ overlapData, isLoading }) => {
  const [displayCount, setDisplayCount] = useState(20);
  const { isPrivateMode } = usePrivacy();
  const { formatAmount } = useCurrency();

  if (isLoading) {
    return (
      <div className="card card-forest mb-4">
        <div className="card-body">
          <div className="section-label mb-3">ETF Overlap</div>
          <div className="d-flex align-items-center justify-content-center py-5">
            <LoadingSpinner text="Analysing overlap..." />
          </div>
        </div>
      </div>
    );
  }

  if (!overlapData || overlapData.etf_count < 2) {
    return (
      <div className="card card-forest mb-4">
        <div className="card-body p-3 p-md-4">
          <div className="section-label mb-2">ETF Overlap</div>
          <div className="d-flex align-items-center justify-content-center py-4" style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>
            Add at least 2 ETFs to see overlap analysis.
          </div>
        </div>
      </div>
    );
  }

  const items = overlapData.overlapping.slice(0, displayCount);
  const maxWeight = overlapData.overlapping[0]?.combined_weight ?? 1;

  return (
    <>
      {/* Similarity heatmap */}
      <ETFSimilarityHeatmap etfs={overlapData.etfs} matrix={overlapData.similarity_matrix} holdingsByEtf={overlapData.holdings_by_etf} />

      {/* Overlapping securities table */}
      {overlapData.overlapping.length === 0 ? (
        <div className="card card-forest mb-4">
          <div className="card-body p-3 p-md-4">
            <div className="section-label mb-2">ETF Overlap</div>
            <div className="d-flex align-items-center justify-content-center py-4" style={{ color: '#a1a1aa', fontSize: '0.875rem' }}>
              No securities are held by more than one of your ETFs.
            </div>
          </div>
        </div>
      ) : (
        <div className="card mb-4" style={{ border: '1px solid #f4f4f5', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f4f4f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span className="fw-semibold" style={{ color: '#000537', fontSize: '0.95rem' }}>ETF Overlap</span>
              <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#a1a1aa' }}>
                {overlapData.overlapping.length} securities held by multiple ETFs
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.75rem', color: '#a1a1aa' }}>Show</span>
              <select
                className="form-select form-select-sm"
                value={displayCount}
                onChange={e => setDisplayCount(Number(e.target.value))}
                style={{ width: '72px', fontSize: '0.8rem' }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 560 }}>
            {/* Header */}
            <div style={{ padding: '10px 20px', display: 'grid', gridTemplateColumns: '1fr 140px 60px 1fr', gap: '12px', borderBottom: '1px solid #f4f4f5' }}>
              <span className="section-label">Security</span>
              <span className="section-label">Combined Weight</span>
              <span className="section-label">ETFs</span>
              <span className="section-label">Breakdown</span>
            </div>

            {items.map((item, idx) => {
              const pct = (item.combined_weight * 100).toFixed(2);
              const amount = item.combined_weight * overlapData.total_etf_value;
              const barWidth = maxWeight > 0 ? (item.combined_weight / maxWeight) * 100 : 0;
              const sortedEtfs = [...item.etfs].sort((a, b) => b.weight_in_etf - a.weight_in_etf);

              return (
                <div
                  key={item.identifier}
                  style={{
                    padding: '11px 20px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 140px 60px 1fr',
                    gap: '12px',
                    alignItems: 'center',
                    borderBottom: idx < items.length - 1 ? '1px solid #f4f4f5' : 'none',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#fafafa')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#18181b', fontFamily: 'monospace' }}>
                      {item.identifier}
                    </div>
                    {item.name && (
                      <div style={{ fontSize: '0.75rem', color: '#a1a1aa', marginTop: '1px' }}>{item.name}</div>
                    )}
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#000537', fontVariantNumeric: 'tabular-nums' }}>
                        {pct}%
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#71717a', fontVariantNumeric: 'tabular-nums' }}>
                        {formatAmount(amount, isPrivateMode, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div style={{ height: '4px', borderRadius: '2px', background: '#f4f4f5', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${barWidth}%`, background: '#000537', borderRadius: '2px', transition: 'width 0.3s ease' }} />
                    </div>
                  </div>

                  <div>
                    <span style={{
                      fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px',
                      borderRadius: '999px', background: '#f4f4f5', color: '#52525b',
                      display: 'inline-block',
                    }}>
                      {item.etfs.length}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {sortedEtfs.map(e => (
                      <span
                        key={e.etf_id}
                        title={e.name}
                        style={{
                          fontSize: '0.72rem', padding: '2px 7px',
                          borderRadius: '4px', background: '#f0f0ff', color: '#000537',
                          fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                        }}
                      >
                        {e.ticker} {(e.weight_in_etf * 100).toFixed(2)}%
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div></div>
        </div>
      )}
    </>
  );
};

export default ETFOverlapSection;
